import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { AppRolesDirective } from '@directives/appRoles/app-roles.directive';
import { CantidadPorEstadoDto } from '@models/base/cantidadPorEstadoDto.mode';
import { ExpedienteService } from '@services/expediente/expediente.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';

// Registrar Chart.js components
Chart.register(...registerables);

@Component({
  standalone: true,
  imports: [
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDividerModule,
    MatButtonModule,
    RouterModule,
    MatIconButton,
    MatIconModule,
    NgxSpinnerModule,
    CommonModule,
    AppRolesDirective
  ],
  selector: 'app-dashboard-expedientes',
  templateUrl: './dashboard-expedientes.component.html',
  styleUrls: ['./dashboard-expedientes.component.css']
})
export class DashboardExpedientesComponent implements AfterViewInit, OnDestroy {

  @ViewChild('chartEstado') chartEstado!: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartTipo') chartTipo!: ElementRef<HTMLCanvasElement>;

  cardLayout: any
  formBuilder: FormBuilder = new FormBuilder;
  form!: FormGroup;
  cantidadPorEstadoDto: CantidadPorEstadoDto[] = [];
  cantidadPorEstadoPeritoDto: CantidadPorEstadoDto[] = [];
  cantidadPorTipoDto: CantidadPorEstadoDto[] = [];

  private chartEstadoInstance: Chart | null = null;
  private chartTipoInstance: Chart | null = null;

  constructor(
    private expedienteService: ExpedienteService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initCharts();
    }, 1000);
  }

  ngOnInit(): void {
    this.getCantidadPorEstadoDto()
    this.getCantidadPorTipoDto()
  }

  initCharts(): void {
    this.initChartEstado();
    this.initChartTipo();
  }

  initChartEstado(): void {
    if (!this.chartEstado?.nativeElement || this.cantidadPorEstadoDto.length === 0) {
      return;
    }

    const ctx = this.chartEstado.nativeElement.getContext('2d');
    if (!ctx) return;

    if (this.chartEstadoInstance) {
      this.chartEstadoInstance.destroy();
    }

    this.chartEstadoInstance = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: this.cantidadPorEstadoDto.map(item => item.descripcion),
        datasets: [{
          data: this.cantidadPorEstadoDto.map(item => item.cantidad),
          backgroundColor: [
            '#FF6B6B', // Rojo suave
            '#4ECDC4', // Turquesa
            '#45B7D1', // Azul
            '#96CEB4', // Verde agua
            '#FFEAA7', // Amarillo
            '#DDA0DD', // Violeta
            '#FFB347', // Naranja
            '#98FB98'  // Verde claro
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: { size: 11 }
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          }
        }
      }
    });
  }

  initChartTipo(): void {
    if (!this.chartTipo?.nativeElement || this.cantidadPorTipoDto.length === 0) {
      return;
    }

    const ctx = this.chartTipo.nativeElement.getContext('2d');
    if (!ctx) return;

    if (this.chartTipoInstance) {
      this.chartTipoInstance.destroy();
    }

    this.chartTipoInstance = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: this.cantidadPorTipoDto.map(item => item.descripcion),
        datasets: [{
          data: this.cantidadPorTipoDto.map(item => item.cantidad),
          backgroundColor: [
            '#8E44AD', // Púrpura
            '#3498DB', // Azul brillante
            '#E74C3C', // Rojo brillante
            '#2ECC71', // Verde brillante
            '#F39C12', // Naranja brillante
            '#9B59B6', // Violeta brillante
            '#1ABC9C', // Turquesa brillante
            '#34495E'  // Gris azulado
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: { size: 11 }
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          }
        }
      }
    });
  }

  updateCharts(): void {
    if (this.cantidadPorEstadoDto.length > 0) {
      setTimeout(() => this.initChartEstado(), 200);
    }
    if (this.cantidadPorTipoDto.length > 0) {
      setTimeout(() => this.initChartTipo(), 200);
    }
  }

  calcularCantidadTotalExpedientePorEstadoPerito(): number {
    let cantidadTotal = 0;
    for (const item of this.cantidadPorEstadoPeritoDto) {
      cantidadTotal += item.cantidad;
    }
    return cantidadTotal;
  }

  getCantidadPorEstadoDto() {
    this.spinner.show("spCantidadPorEstado")
    this.expedienteService.getCantidadPorEstadoDto().subscribe(
      data => {
        this.spinner.hide("spCantidadPorEstado")
        this.cantidadPorEstadoDto = data
        console.log('Datos Estado recibidos:', data);
        setTimeout(() => this.initChartEstado(), 300);
      },
      error => {
        this.spinner.hide("spCantidadPorEstado")
        this.snackBar.showError(error, "Error");
      })
  }

  calcularCantidadTotalExpedientePorEstado(): number {
    let cantidadTotal = 0;
    for (const item of this.cantidadPorEstadoDto) {
      cantidadTotal += item.cantidad;
    }
    return cantidadTotal;
  }

  getCantidadPorTipoDto() {
    this.spinner.show("spCantidadPorTipo")
    this.expedienteService.getCantidadPorTipoDto().subscribe(
      data => {
        this.spinner.hide("spCantidadPorTipo")
        this.cantidadPorTipoDto = data
        console.log('Datos Tipo recibidos:', data);
        setTimeout(() => this.initChartTipo(), 300);
      },
      error => {
        this.spinner.hide("spCantidadPorTipo")
        this.snackBar.showError(error, "Error");
      })
  }

  calcularCantidadTotalPorTipo(): number {
    let cantidadTotal = 0;
    for (const item of this.cantidadPorTipoDto) {
      cantidadTotal += item.cantidad;
    }
    return cantidadTotal;
  }

  ngOnDestroy(): void {
    if (this.chartEstadoInstance) {
      this.chartEstadoInstance.destroy();
    }
    if (this.chartTipoInstance) {
      this.chartTipoInstance.destroy();
    }
  }

  // DEBUG TEMPORAL
  forceCreateCharts(): void {
    console.log('=== FORZAR GRÁFICOS ===');
    console.log('Estado datos:', this.cantidadPorEstadoDto);
    console.log('Tipo datos:', this.cantidadPorTipoDto);
    this.updateCharts();
  }
}
